/* ==========================================================================
   FLC Forge — Predictive Search
   ========================================================================== */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input        = this.querySelector('[data-search-input]');
    this.clearBtn     = this.querySelector('[data-search-clear]');
    this.panel        = this.querySelector('[data-search-panel]');
    this.leftLabel    = this.querySelector('[data-search-left-label]');
    this.leftContent  = this.querySelector('[data-search-left-content]');
    this.rightLabel   = this.querySelector('[data-search-right-label]');
    this.resultsGrid  = this.querySelector('[data-search-results]');
    this.footerEl     = this.querySelector('[data-search-footer]');
    this.viewAllLink  = this.querySelector('[data-search-view-all]');
    this.viewAllLabel = this.querySelector('[data-search-view-all-label]');
    this.searchBar    = this.closest('.header-search');
    this.showProducts = this.dataset.showProducts === 'true';
    this.abort        = null;

    // Save Liquid-rendered default state
    this.defaultLeftLabel   = this.leftLabel?.textContent || '';
    this.defaultLeftHTML    = this.leftContent?.innerHTML || '';
    this.defaultRightLabel  = this.rightLabel?.textContent || '';
    this.defaultResultsHTML = this.resultsGrid?.innerHTML || '';

    this.input?.addEventListener('input', FLC.debounce(() => this.handleInput(), 300));
    this.input?.addEventListener('focus', () => {
      if (this.input?.value.trim()) this.openPanel();
    });
    this.clearBtn?.addEventListener('click', () => this.clearSearch());

    // Close panel when SearchToggle removes is-open
    if (this.searchBar) {
      new MutationObserver(() => {
        if (!this.searchBar.classList.contains('is-open')) this.closePanel();
      }).observe(this.searchBar, { attributes: true, attributeFilter: ['class'] });
    }

    document.addEventListener('click', (e) => {
      if (!this.searchBar?.contains(e.target)) this.closePanel();
    });
  }

  openPanel() {
    this.panel?.removeAttribute('hidden');
  }

  closePanel() {
    this.panel?.setAttribute('hidden', '');
    this.footerEl?.setAttribute('hidden', '');
  }

  clearSearch() {
    if (this.input) this.input.value = '';
    this.clearBtn?.classList.remove('is-visible');
    this.restoreDefaults();
    this.closePanel();
    this.input?.focus();
  }

  restoreDefaults() {
    if (this.leftLabel)    this.leftLabel.textContent    = this.defaultLeftLabel;
    if (this.leftContent)  this.leftContent.innerHTML    = this.defaultLeftHTML;
    if (this.rightLabel)   this.rightLabel.textContent   = this.defaultRightLabel;
    if (this.resultsGrid)  this.resultsGrid.innerHTML    = this.defaultResultsHTML;
    this.panel?.removeAttribute('data-has-query');
    this.footerEl?.setAttribute('hidden', '');
    this.abort?.abort();
  }

  async handleInput() {
    const query = this.input?.value.trim();

    this.clearBtn?.classList.toggle('is-visible', !!query);

    if (!query) {
      this.restoreDefaults();
      this.closePanel();
      return;
    }

    this.openPanel();
    this.panel?.setAttribute('data-has-query', '');
    this.setLoading();
    this.abort?.abort();
    this.abort = new AbortController();

    try {
      const resourceType = this.showProducts ? 'product,query' : 'query';
      const url = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&resources[type]=${resourceType}&resources[limit]=6`;
      const res = await fetch(url, {
        signal: this.abort.signal,
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data   = await res.json();
      const results = data.resources?.results || {};
      this.renderSuggestions(results.queries || [], query);
      if (this.showProducts) this.renderProducts(results.products || [], query);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Predictive search error:', err);
        this.restoreDefaults();
      }
    }
  }

  setLoading() {
    if (this.resultsGrid) {
      this.resultsGrid.innerHTML = '<div class="search-panel__loading" role="status" aria-label="Searching\u2026"></div>';
    }
  }

  renderSuggestions(queries, query) {
    if (this.leftLabel) this.leftLabel.textContent = 'Suggestions';
    if (!this.leftContent) return;

    if (!queries.length) {
      this.leftContent.innerHTML = '';
      return;
    }

    this.leftContent.innerHTML = queries.map(q => {
      // Use styled_text (has <b> tags) if available, otherwise plain text
      const label = q.styled_text || this.esc(q.text);
      const href  = `${this.dataset.resultsUrl}?q=${encodeURIComponent(q.text)}&type=${this.dataset.searchType || 'product'}`;
      return `<li><a href="${href}" class="search-panel__list-link">${label}</a></li>`;
    }).join('');
  }

  renderProducts(products, query) {
    if (this.rightLabel) this.rightLabel.textContent = 'Products';
    if (!this.resultsGrid) return;

    if (!products.length) {
      this.resultsGrid.innerHTML = `<p class="search-panel__no-results">No results for &ldquo;${this.esc(query)}&rdquo;</p>`;
      this.footerEl?.setAttribute('hidden', '');
      return;
    }

    this.resultsGrid.innerHTML = products.map(p => {
      const imgUrl = this.resizeImage(p.featured_image?.url, 400);
      const price  = p.price != null ? FLC.formatMoney(p.price) : '';
      return `
        <a href="${p.url}" class="search-product-card">
          <div class="search-product-card__media">
            ${imgUrl
              ? `<img src="${imgUrl}" alt="${this.esc(p.featured_image?.alt || p.title)}" loading="lazy">`
              : ''
            }
          </div>
          <p class="search-product-card__title">${this.esc(p.title)}</p>
          ${price ? `<p class="search-product-card__price">${price}</p>` : ''}
        </a>`;
    }).join('');

    if (this.viewAllLink && this.viewAllLabel) {
      this.viewAllLink.href = `${this.dataset.resultsUrl}?q=${encodeURIComponent(query)}&type=${this.dataset.searchType || 'product'}`;
      this.viewAllLabel.textContent = `Search for \u201c${query}\u201d`;
    }
    this.footerEl?.removeAttribute('hidden');
  }

  resizeImage(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch {
      return url;
    }
  }

  esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

customElements.define('predictive-search', PredictiveSearch);
